import React, { useEffect, useRef, useState } from 'react';
import {
  IconChart,
  IconInstant,
  IconPending,
  IconSuccess,
  IconVoiceCall,
} from '../components/icons';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/Card';
import Button from '../components/Button';
import { Label, Input, Select } from '../components/Input';
import Alert from '../components/Alert';
import PatientSelector from '../components/PatientSelector';
import { elevenLabsAPI, toE164 } from '../services/api';
import { useAuth } from '../context/AuthContext';

const VoiceCalls = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [callResult, setCallResult] = useState(null);
  const [selectedPatients, setSelectedPatients] = useState([]);

  const [formData, setFormData] = useState({
    campaignName: '',
    healthTopic: '',
    campaignGoal: '',
    delayBetweenCalls: '2',
  });

  // Calls are placed in the background, so the POST response only says what was
  // queued. Without polling the console could never show what actually
  // happened — which is most of why a broken campaign looked like a fine one.
  const pollTimer = useRef(null);
  // A run id, so a superseded chain stops instead of clearing the newer
  // campaign's timer — and so an in-flight await cannot resume after unmount.
  const pollRun = useRef(0);

  useEffect(() => () => {
    pollRun.current += 1;
    clearTimeout(pollTimer.current);
  }, []);

  const pollCampaign = (campaignId, deadline, run, failures = 0) => {
    if (run !== pollRun.current) return;
    clearTimeout(pollTimer.current);
    pollTimer.current = setTimeout(async () => {
      if (run !== pollRun.current) return;
      try {
        const status = await elevenLabsAPI.getCampaignStatus(campaignId);
        if (run !== pollRun.current) return;
        setCallResult((prev) =>
          prev && prev.campaignId === campaignId
            ? { ...prev, live: status, polling: Date.now() < deadline }
            : prev
        );
        const done = String(status?.status || '').toLowerCase() === 'completed';
        if (!done && Date.now() < deadline) {
          pollCampaign(campaignId, deadline, run, 0);
        } else if (!done) {
          // Budget spent while still running — stop claiming it is live.
          setCallResult((prev) =>
            prev && prev.campaignId === campaignId ? { ...prev, polling: false } : prev
          );
        }
      } catch {
        // A failed poll is not a failed campaign — retry a few times before
        // giving up, rather than abandoning the campaign on one blip.
        if (failures < 3 && Date.now() < deadline) {
          pollCampaign(campaignId, deadline, run, failures + 1);
        } else if (run === pollRun.current) {
          setCallResult((prev) =>
            prev && prev.campaignId === campaignId ? { ...prev, polling: false } : prev
          );
        }
      }
    }, 5000);
  };

  const healthTopics = [
    'Malaria Prevention and Treatment',
    'Preeclampsia Awareness',
    'Maternal Health',
    'HIV/AIDS Prevention',
    'Diabetes Management',
    'Hypertension Control',
    'Tuberculosis Prevention',
    'COVID-19 Safety',
    'Child Nutrition',
    'Mental Health',
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlert(null);
    setLoading(true);

    try {
      if (selectedPatients.length === 0) {
        throw new Error('Please select at least one patient');
      }

      // Normalize to E.164 (+233XXXXXXXXX) for the SIP trunk, and keep track of
      // who could not be normalized. toE164 returns '' for an unparseable
      // number, and sending that blank through meant the backend dialled the
      // bare country code. Drop them here and say so.
      const normalized = selectedPatients.map((p) => ({
        patient: p,
        phone: toE164(p.phone),
      }));
      const recipients = normalized.filter((r) => r.phone).map((r) => r.phone);
      const unusable = normalized.filter((r) => !r.phone);

      if (recipients.length === 0) {
        throw new Error(
          'None of the selected patients have a usable phone number. ' +
            'Numbers must be Ghanaian (0XXXXXXXXX or +233XXXXXXXXX).'
        );
      }

      const result = await elevenLabsAPI.makeBulkCalls({
        recipients,
        campaign_name: formData.campaignName,
        health_topic: formData.healthTopic,
        campaign_goal: formData.campaignGoal || undefined,
        delay_between_calls: Number(formData.delayBetweenCalls) || 0,
        user_id: user?.id ?? null,
      });

      // Read what the API actually returned. `calls_initiated`/`calls_failed`
      // were never keys it sends, so this panel used to report the number we
      // hoped for and zero failures, every time.
      const queued = result.recipients_count ?? recipients.length;
      const rejectedByServer = result.rejected_count ?? 0;
      setCallResult({
        campaignId: result.campaign_id,
        campaignName: result.campaign_name || formData.campaignName,
        total: selectedPatients.length,
        queued,
        skipped: unusable.length + rejectedByServer,
        skippedNames: unusable.map((r) => r.patient.name || r.patient.phone),
        rejected: result.rejected_recipients || [],
        agentId: result.agent_id,
        durationCapEnforced: result.duration_cap_enforced,
        // Filled in by the poller below once calls start landing.
        live: null,
      });

      if (result.campaign_id) {
        // Size the budget from the campaign the API says it queued, plus
        // headroom — a fixed 5 minutes was routinely shorter than the run it
        // was tracking, so the panel stopped updating mid-campaign.
        const estMinutes = Number(result.estimated_duration_minutes) || 1;
        const deadline = Date.now() + Math.min(60, estMinutes * 2 + 5) * 60_000;
        pollRun.current += 1;
        pollCampaign(result.campaign_id, deadline, pollRun.current);
      }

      setAlert({
        type: unusable.length || rejectedByServer ? 'warning' : 'success',
        title: 'AI Voice Campaign Started',
        message:
          `Calling ${queued} patient(s) for "${result.campaign_name || formData.campaignName}".` +
          (unusable.length + rejectedByServer
            ? ` ${unusable.length + rejectedByServer} skipped for an unusable phone number.`
            : ''),
      });

      // Reset
      setFormData({ campaignName: '', healthTopic: '', campaignGoal: '', delayBetweenCalls: '2' });
      setSelectedPatients([]);
    } catch (error) {
      setAlert({
        type: 'error',
        title: 'Campaign Failed',
        message: error.userMessage || error.response?.data?.detail || error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Outbound AI Voice Calls</h2>
        <p className="text-gray-600 mt-1">
          Launch bulk outbound voice campaigns powered by conversational AI
        </p>
      </div>

      {alert && (
        <Alert type={alert.type} title={alert.title} message={alert.message} />
      )}

      {/* Campaign Results */}
      {callResult && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-green-900">Campaign Results</h3>
            <button
              onClick={() => setCallResult(null)}
              className="text-green-600 hover:text-green-800 text-xs"
            >
              Dismiss
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 text-center">
            <div className="bg-white rounded-lg p-3 border border-green-100">
              <p className="text-2xl font-bold text-gray-800">{callResult.total}</p>
              <p className="text-xs text-gray-500">Selected</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-green-100">
              <p className="text-2xl font-bold text-blue-700">{callResult.queued}</p>
              <p className="text-xs text-gray-500">Queued</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-green-100">
              <p className="text-2xl font-bold text-blue-700">
                {callResult.live?.dispatched ?? '—'}
              </p>
              <p className="text-xs text-gray-500">Dialled</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-green-100">
              <p className="text-2xl font-bold text-green-700">
                {callResult.live?.answered ?? '—'}
              </p>
              <p className="text-xs text-gray-500">Answered</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-green-100">
              <p className="text-2xl font-bold text-red-600">
                {callResult.live?.failed ?? '—'}
              </p>
              <p className="text-xs text-gray-500">Failed</p>
            </div>
          </div>

          {/* A call that simply rang out is neither a failure nor a success,
              and used to be counted in neither tile. */}
          {callResult.live?.noAnswer > 0 && (
            <p className="text-xs text-gray-600 mt-2">
              {callResult.live.noAnswer} patient(s) did not pick up.
            </p>
          )}

          {callResult.live ? (
            <>
              <p className="text-xs text-green-700 mt-2">
                Status: <span className="font-medium">{callResult.live.status}</span>
                {callResult.live.status !== 'completed' &&
                  (callResult.polling ? ' — updating live…' : ' — updates stopped; reload to refresh')}
              </p>
              {/* A batch abandoned part-way (paused, provider misconfigured,
                  provider down) must not render as one that simply finished. */}
              {callResult.live.outcome?.aborted_reason && (
                <p className="text-xs text-red-700 mt-1 font-medium">
                  Campaign stopped early: {callResult.live.outcome.aborted_reason}
                  {callResult.live.outcome.not_attempted > 0 &&
                    ` — ${callResult.live.outcome.not_attempted} patient(s) were never called.`}
                </p>
              )}
            </>
          ) : (
            <p className="text-xs text-green-700 mt-2">Waiting for the first call to report…</p>
          )}

          {callResult.skipped > 0 && (
            <p className="text-xs text-amber-700 mt-2">
              {callResult.skipped} patient(s) skipped for an unusable phone number
              {callResult.skippedNames?.length
                ? `: ${callResult.skippedNames.slice(0, 5).join(', ')}`
                : ''}
              {callResult.skippedNames?.length > 5 ? '…' : ''}
            </p>
          )}

          {/* The API sends a reason per rejected row; showing "unusable phone
              number" for all of them hides duplicates, which are a different
              problem with a different fix. */}
          {callResult.rejected?.length > 0 && (
            <ul className="text-xs text-amber-700 mt-1 space-y-0.5">
              {callResult.rejected.slice(0, 5).map((r, i) => (
                <li key={`${r.recipient}-${i}`}>
                  <span className="font-mono">{r.recipient || '(blank)'}</span> — {r.reason}
                </li>
              ))}
              {callResult.rejected.length > 5 && (
                <li>…and {callResult.rejected.length - 5} more.</li>
              )}
            </ul>
          )}

          {callResult.durationCapEnforced === false && (
            <p className="text-xs text-amber-700 mt-1">
              Warning: this agent ignores the per-call duration cap, so long calls
              will bill at the agent&apos;s own limit.
            </p>
          )}

          {callResult.agentId && (
            <p className="text-xs text-green-700 mt-2">
              Agent ID: <span className="font-mono">{callResult.agentId}</span>
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Create Bulk AI Voice Campaign</CardTitle>
              <CardDescription>
                Launch bulk outbound calls using conversational AI agents
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Campaign Name */}
                <div>
                  <Label htmlFor="campaignName">Campaign Name *</Label>
                  <Input
                    id="campaignName"
                    name="campaignName"
                    placeholder="e.g., Malaria Prevention Campaign 2026"
                    value={formData.campaignName}
                    onChange={handleChange}
                    required
                  />
                </div>

                {/* Patient Selector */}
                <div>
                  <Label>
                    Patients *{' '}
                    {selectedPatients.length > 0 && (
                      <span className="text-gray-500 font-normal">
                        ({selectedPatients.length} selected)
                      </span>
                    )}
                  </Label>
                  <div className="mt-1">
                    <PatientSelector
                      onSelect={setSelectedPatients}
                      title="Select patients to call"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Phone numbers are automatically formatted to E.164 (+233XXXXXXXXX) for the
                    SIP trunk. Patients without a usable number are skipped and listed after
                    you start the campaign.
                  </p>
                </div>

                {/* Health Topic */}
                <div>
                  <Label htmlFor="healthTopic">Health Topic *</Label>
                  <Select
                    id="healthTopic"
                    name="healthTopic"
                    value={formData.healthTopic}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a topic…</option>
                    {healthTopics.map((topic) => (
                      <option key={topic} value={topic}>
                        {topic}
                      </option>
                    ))}
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    The AI agent will educate callers about this health topic.
                  </p>
                </div>

                {/* Campaign Goal */}
                <div>
                  <Label htmlFor="campaignGoal">Campaign Goal</Label>
                  <Input
                    id="campaignGoal"
                    name="campaignGoal"
                    placeholder="e.g., Learn malaria symptoms and when to seek care"
                    value={formData.campaignGoal}
                    onChange={handleChange}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Keeps the agent on-purpose once the patient starts talking back.
                    Without it the agent falls back to its general inbound persona.
                  </p>
                </div>

                {/* Delay Between Calls */}
                <div>
                  <Label htmlFor="delayBetweenCalls">Delay Between Calls (seconds)</Label>
                  <Input
                    id="delayBetweenCalls"
                    name="delayBetweenCalls"
                    type="number"
                    min="1"
                    max="60"
                    value={formData.delayBetweenCalls}
                    onChange={handleChange}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Recommended: 2–5 seconds to avoid overwhelming the system.
                  </p>
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  loading={loading}
                  disabled={selectedPatients.length === 0}
                  className="w-full"
                >
                  <IconVoiceCall className="h-5 w-5 mr-2" />
                  Launch AI Voice Campaign
                  {selectedPatients.length > 0 && ` (${selectedPatients.length} patients)`}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Campaign Flow */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-base">Campaign Flow</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                {[
                  { n: 1, title: 'Patient Selection', desc: 'Choose patients from your database to contact' },
                  { n: 2, title: 'Bulk Call Initiation', desc: 'AI agent initiates calls sequentially via SIP trunk' },
                  { n: 3, title: 'AI Conversations', desc: 'Natural speech-to-speech health education interactions' },
                  { n: 4, title: 'Campaign Tracking', desc: 'Monitor call status and patient engagement' },
                ].map(({ n, title, desc }, i, arr) => (
                  <div key={n} className={`flex items-start space-x-3 ${i < arr.length - 1 ? 'pb-3 border-b' : ''}`}>
                    <div className="w-8 h-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center flex-shrink-0 text-xs font-bold">
                      {n}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{title}</p>
                      <p className="text-gray-600">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Info Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Features</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              {[
                { Icon: IconInstant, color: 'text-green-600', title: 'Conversational AI Agents', desc: 'Natural speech-to-speech conversations' },
                { Icon: IconVoiceCall, color: 'text-blue-600', title: 'Bulk Outbound Calls', desc: 'Reach multiple patients efficiently' },
                { Icon: IconChart, color: 'text-blue-600', title: 'Personalized', desc: 'AI adapts to each patient\'s responses' },
              ].map(({ Icon, color, title, desc }) => (
                <div key={title} className="flex items-start space-x-3">
                  <Icon className={`h-5 w-5 ${color} flex-shrink-0 mt-0.5`} />
                  <div>
                    <p className="font-medium text-gray-900">{title}</p>
                    <p className="text-gray-600">{desc}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Best Practices</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-gray-700">
              {[
                'Test with your own number first',
                'Choose appropriate time to call (9am – 7pm)',
                'Verify patient records before calling',
                'Keep conversations focused on health education',
              ].map((tip) => (
                <div key={tip} className="flex items-start space-x-2">
                  <IconSuccess className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <p>{tip}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Call Timing</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-gray-700">
              {[
                'Calls are placed sequentially with your configured delay',
                'Campaign runs in the background — no need to keep the page open',
              ].map((tip) => (
                <div key={tip} className="flex items-start space-x-2">
                  <IconPending className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p>{tip}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default VoiceCalls;
